import React, { useState, useRef, useEffect } from 'react';
import styled from '@emotion/styled';
import { Input, Box, Text, Palette, Flex } from '@updater/ui-uds';
import { MapPin, X } from '@phosphor-icons/react';
import { useCombobox } from 'downshift';
import { Address } from 'types';
import { SmartyStreetsAutocompleteSuggestion } from 'hooks/address';
import { useSingleLineAddressInputContext } from './context';

const SuggestionsList = styled.ul<{ isOpen: boolean }>`
  width: 100%;
  top: 64px;
  margin: 0;
  position: absolute;
  z-index: ${({ theme }) => theme.zIndices.interaction};
  max-height: 300px;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.white};
  list-style: none;
  border: 1px solid ${({ theme }) => theme.input.borderHoverColor};
  border-radius: ${({ theme }) => theme.radii.xs};
  border-top-right-radius: 0;
  border-top-left-radius: 0;
  border-top: 1px solid transparent;
  box-shadow: 0px 4px 4px rgba(117, 117, 117, 0.25);
  padding-inline-start: 0;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

const SuggestionItem = styled.li<{ isHighlighted: boolean }>`
  cursor: pointer;
  padding: ${({ theme }) => `${theme.space.xs}px ${theme.space.s}px`};
  display: grid;
  grid-template-columns: min-content 1fr;
  grid-template-areas:
    'mapPin primaryText'
    'mapPin secondaryText';
  gap: 0 ${({ theme }) => theme.space.xs}px;
  align-items: center;
  background-color: ${({ isHighlighted, theme }) =>
    isHighlighted ? theme.colors.itemHoverBackground : 'transparent'};

  &:hover {
    background-color: ${({ theme }) => theme.colors.itemHoverBackground};
  }
`;

const CloseIconButton = styled.button`
  height: ${({ theme }) => `${theme.space.m}px`};
  width: ${({ theme }) => `${theme.space.m}px`};
  border-radius: ${({ theme }) => theme.radii['50%']};
  background-color: ${({ theme }) => theme.colors.itemHoverBackground};
  border: none;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

export interface TypeaheadInputProps {
  label?: string;
  placeholder?: string;
  error?: string;
  name?: string;
}

function suggestionToAddress(
  suggestion: SmartyStreetsAutocompleteSuggestion
): Address {
  return {
    street: suggestion.streetLine || '',
    // entries > 1 means multiple units need expansion; entries === 1 has the exact unit in secondary
    unit: suggestion.entries > 1 ? '' : suggestion.secondary || '',
    city: suggestion.city || '',
    state: suggestion.state || '',
    postalCode: suggestion.zipcode || '',
  };
}

function formatAddress(address: Address): string {
  return [
    address.street,
    `${address.city}, ${address.state} ${address.postalCode}`,
  ].join(', ');
}

function formatSuggestion(
  suggestion: SmartyStreetsAutocompleteSuggestion
): string {
  const street = [suggestion.streetLine, suggestion.secondary]
    .filter(Boolean)
    .join(' ');
  return [
    street,
    `${suggestion.city}, ${suggestion.state} ${suggestion.zipcode}`,
  ].join(', ');
}

export function TypeaheadInput({
  label = 'Street address, city, state, zip',
  placeholder,
  error,
  name = 'address',
}: TypeaheadInputProps) {
  const {
    suggestions,
    onSearchAddress,
    clearSuggestions,
    onSelectAddress,
    onReset,
    state,
    data,
  } = useSingleLineAddressInputContext();

  const [inputValue, setInputValue] = useState('');
  const [showClearButton, setShowClearButton] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Populate input when address is set from data (e.g. initialAddress)
  useEffect(() => {
    if (data.currentAddress && state !== 'empty') {
      setInputValue(formatAddress(data.currentAddress));
      setShowClearButton(true);
    }
  }, [data.currentAddress, state]);

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    highlightedIndex,
    closeMenu,
  } = useCombobox({
    items: suggestions,
    inputValue,
    onSelectedItemChange: ({ selectedItem }) => {
      if (!selectedItem) return;

      const address = suggestionToAddress(selectedItem);
      setInputValue(formatSuggestion(selectedItem));
      onSelectAddress(address);
      clearSuggestions();
      closeMenu();
    },
    itemToString: (item) => (item ? formatSuggestion(item) : ''),
    stateReducer: (_state, { type, changes }) => {
      if (type === useCombobox.stateChangeTypes.InputChange) {
        return {
          ...changes,
          highlightedIndex: changes.isOpen ? 0 : changes.highlightedIndex,
        };
      }
      return changes;
    },
  });

  const handleClear = () => {
    setInputValue('');
    setShowClearButton(false);
    onReset();
    clearSuggestions();
    closeMenu();
    inputRef.current?.focus();
  };

  const handleFocus = () => {
    setIsFocused(true);
    if (inputValue) {
      inputRef.current?.select();
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!inputValue) {
      setShowClearButton(false);
    }
  };

  const placeholderProps = placeholder
    ? { placeholder, className: 'input-with-placeholder' }
    : {};

  return (
    <Box
      position="relative"
      zIndex={isFocused ? 'interaction' : 'initial'}
      {...getComboboxProps()}
    >
      <Input
        {...getLabelProps()}
        {...getInputProps({
          ref: inputRef,
          refKey: 'inputRef',
          onFocus: handleFocus,
          onBlur: handleBlur,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value;
            setInputValue(newValue);
            setShowClearButton(newValue.length > 0);
            if (state !== 'empty') onReset();
            onSearchAddress(newValue);
          },
        })}
        label={label}
        {...placeholderProps}
        error={error}
        name={name}
        value={inputValue}
        isDropDownOpen={isOpen && suggestions.length > 0}
        autoComplete="on"
        rightSlot={
          showClearButton ? (
            <CloseIconButton
              onClick={handleClear}
              type="button"
              aria-label="clear address"
              data-testid="clear-address-button"
            >
              <X size={20} weight="thin" style={{ color: 'inherit' }} />
            </CloseIconButton>
          ) : null
        }
      />

      <SuggestionsList
        {...getMenuProps()}
        isOpen={isOpen && suggestions.length > 0}
      >
        {isOpen &&
          suggestions.map((suggestion, index) => (
            <SuggestionItem
              key={`${suggestion.streetLine}-${suggestion.city}-${index}`}
              isHighlighted={highlightedIndex === index}
              {...getItemProps({ item: suggestion, index })}
            >
              <Flex gridArea="mapPin" display="flex" alignItems="center">
                <MapPin size={16} color={Palette.blue500} />
              </Flex>
              <Text gridArea="primaryText" variant="s">
                {suggestion.streetLine}
                {suggestion.secondary ? ` ${suggestion.secondary}` : ''}
              </Text>
              <Text gridArea="secondaryText" variant="xs" color="textSoft">
                {suggestion.city}, {suggestion.state} {suggestion.zipcode}
              </Text>
            </SuggestionItem>
          ))}
      </SuggestionsList>
    </Box>
  );
}
